import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { multipleprintsLinksColumnsVisibility } from "@/config/dashboard/multipleprints";
import { FormLayoutElement } from "@/config/forms/general";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import {
  Collector,
  CollectorInMultipleprint,
  Multipleprint,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { MultipleprintLinkGridCard } from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import CollectorInMultipleprintDrawer from "./drawer/CollectorInMultipleDrawer";

interface ComplexLinkProps {
  id: string;
  collector: Collector;
  fields: FormLayoutElement[] | undefined;
  type: LinkableType;
}

const CollectorInMultipleprints = ({
  id,
  collector,
  fields,
  type,
}: ComplexLinkProps) => {
  const { t } = useTranslation();
  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "collectorInMultipleprints",
    type,
  );

  const { mutate: deleteCollectorInMultipleprint } = useDeleteComplexLink(
    id,
    "collectorInMultipleprints",
    type,
  );

  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (id: string) => {
      deleteCollectorInMultipleprint({
        id,
        relation: "collectorInMultipleprint",
      });
    },
    [deleteCollectorInMultipleprint],
  );

  const [searchContent, setSearchContent] = useState<string>("");

  const renderCollectorInMultipleprintRowActions = useCallback(
    (data: CollectorInMultipleprint) => (
      <LinkDrawerRowActions
        previewType="multipleprint"
        previewId={data.multipleprint.id}
        previewElements={[
          {
            label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
            value:
              data.multipleprint.estampesMultiplesDescriptionNumeroEnregistrement,
            type: "text",
          },
          {
            label: t("estampesMultiplesDescriptionResume"),
            value: data.multipleprint.estampesMultiplesDescriptionResume,
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: data.multipleprint.attachments,
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(data.id)}
        editDrawer={({ open, onOpenChange }) => (
          <CollectorInMultipleprintDrawer
            key={data.id}
            fields={fields}
            data={data}
            queryId={id}
            type={type}
            open={open}
            onOpenChange={onOpenChange}
          />
        )}
      />
    ),
    [fields, handleDeleteRelation, id, t, type],
  );

  const columns: ColumnDef<CollectorInMultipleprint, any>[] = useMemo(() => {
    return [
      {
        id: "estampesMultiplesDescriptionNumeroEnregistrement",
        accessorFn: (row: CollectorInMultipleprint) =>
          row.multipleprint?.estampesMultiplesDescriptionNumeroEnregistrement ?? "",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: (props: { getValue: () => unknown }) => (
          <TextCell value={String(props.getValue() ?? "")} />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<CollectorInMultipleprint> }) => {
          return (
            <ImageCell
              value=""
              attachments={row.original.multipleprint?.attachments ?? []}
              domain="multipleprint"
            />
          );
        },
      },
      {
        id: "estampesMultiplesDescriptionTitre",
        accessorFn: (row: CollectorInMultipleprint) =>
          row.multipleprint?.estampesMultiplesDescriptionTitre ?? "",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: (props: { getValue: () => unknown }) => (
          <HtmlCell value={String(props.getValue() ?? "")} />
        ),
      },
      {
        id: "estampesMultiplesDescriptionTechnique",
        accessorFn: (row: CollectorInMultipleprint) =>
          row.multipleprint?.estampesMultiplesDescriptionTechnique?.[
            getMainLanguage() as SupportedLocale
          ] ?? "",
        header: t("estampesMultiplesDescriptionTechnique"),
        cell: (props: { getValue: () => unknown }) => (
          <TextCell value={String(props.getValue() ?? "")} />
        ),
      },
      {
        accessorKey: "estampesDansCollectionneursDescriptionInfos",
        header: t("estampesDansCollectionneursDescriptionInfos"),
      },
      createActionsColumnDef<CollectorInMultipleprint>(({ row }) =>
        renderCollectorInMultipleprintRowActions(row.original),
      ),
    ];
  }, [renderCollectorInMultipleprintRowActions, t]);

  return (
    <div className="w-full">
      <CollectorInMultipleprintLinks
        id={id}
        relatedData={relatedData}
        fields={fields}
        collector={collector}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderCollectorInMultipleprintRowActions}
        searchContent={searchContent}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        listGridTitle={t("linkedMultipleprints")}
        layoutBackLabel={t("linkedEntries")}
        setIsAddMode={setIsAddMode}
      />
    </div>
  );
};

type CollectorInMultipleprintLinksProps =
  LinksTableProps<CollectorInMultipleprint> & {
    collector: Collector | undefined;
  };

//Composant pour Oeuvres
const CollectorInMultipleprintLinks = ({
  id,
  relatedData,
  fields,
  collector,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: CollectorInMultipleprintLinksProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "collector-multipleprint-add";
  const STORAGE_KEY_LINKED = "collector-multipleprint-linked";
  const defaultVisibility = { ...multipleprintsLinksColumnsVisibility };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notRelatedMultiples } = useGetNotRelatedResource<Multipleprint>(
    {
      resultType: "multiple",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsMultipleprints: ColumnDef<Multipleprint, any>[] = useMemo(() => {
    return [
      {
        accessorKey: "estampesMultiplesDescriptionNumeroEnregistrement",
        header: t("estampesMultiplesDescriptionNumeroEnregistrement"),
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }) => {
          return (
            <ImageCell
              value={""}
              attachments={row.original.attachments ?? []}
              domain={"multipleprint"}
            />
          );
        },
      },
      {
        accessorKey: "estampesMultiplesDescriptionTitre",
        header: t("estampesMultiplesDescriptionTitre"),
        cell: (props) => {
          return <HtmlCell value={String(props.getValue() ?? "")} />;
        },
      },
      {
        id: "estampesMultiplesDescriptionTechnique",
        accessorFn: (row: Multipleprint) =>
          row.estampesMultiplesDescriptionTechnique?.[
            getMainLanguage() as SupportedLocale
          ] ?? "",
        header: t("estampesMultiplesDescriptionTechnique"),
        size: 100,
        cell: (props) => {
          return <TextCell value={String(props.getValue() ?? "")} />;
        },
      },
      createActionsColumnDef<Multipleprint>(({ row }) => {
          const data = row.original as Multipleprint;
          if (!collector) return null;
          return (
            <LinkDrawerRowActions
              previewType="multipleprint"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("estampesMultiplesDescriptionNumeroEnregistrement"),
                  value: data.estampesMultiplesDescriptionNumeroEnregistrement,
                  type: "text",
                },
                {
                  label: t("estampesMultiplesDescriptionResume"),
                  value: data.estampesMultiplesDescriptionResume,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: data.attachments,
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <CollectorInMultipleprintDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={collector.id}
                  type="collector"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    collector: collector,
                    multipleprint: data,
                    estampesDansCollectionneursDescriptionInfos: "",
                  }}
                />
              )}
            />
          );
        }),
    ];
  }, [collector, fields, t]);

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsMultipleprints} data={notRelatedMultiples?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notRelatedMultiples?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultipleprintLinkGridCard
                    item={row.multipleprint}
                    t={t}
                    onTextClick={() => setSelectedRelationId(row.id)}
                  />
                </GridCardWithActions>
                <CollectorInMultipleprintDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="collector"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default CollectorInMultipleprints;
